/**
 * ===== UNIT CONVERTER - CONVERSION D'UNITÉS =====
 * Convertisseur complet pour les unités de mesure
 */

export type UnitType = 'length' | 'angle' | 'time' | 'weight' | 'temperature';
export type LengthUnit = 'px' | 'pt' | 'in' | 'cm' | 'mm' | 'pc' | 'em' | 'rem' | 'vw' | 'vh' | '%';
export type AngleUnit = 'deg' | 'rad' | 'grad' | 'turn';
export type TimeUnit = 'ms' | 's' | 'min' | 'h';
export type WeightUnit = 'g' | 'kg' | 'lb' | 'oz';
export type TemperatureUnit = 'c' | 'f' | 'k';

export interface ConversionResult {
  value: number;
  unit: string;
  originalValue: number;
  originalUnit: string;
  precision: number;
}

export interface ConversionOptions {
  precision?: number;
  roundToNearest?: number;
  formatOutput?: boolean;
}

export class UnitConverter {
  private static instance: UnitConverter;
  private defaultOptions: ConversionOptions = {
    precision: 6,
    roundToNearest: 0.01,
    formatOutput: true,
  };

  private constructor() {}

  public static getInstance(): UnitConverter {
    if (!UnitConverter.instance) {
      UnitConverter.instance = new UnitConverter();
    }
    return UnitConverter.instance;
  }

  /**
   * Convertit une valeur de longueur
   */
  public convertLength(
    value: number,
    from: LengthUnit,
    to: LengthUnit,
    options: ConversionOptions = {}
  ): ConversionResult {
    const opts = { ...this.defaultOptions, ...options };
    const pixels = this.toPixels(value, from);
    const result = this.fromPixels(pixels, to);
    const precision = opts.precision ?? this.defaultOptions.precision ?? 6;
    const roundedValue = this.roundValue(result, precision, opts.roundToNearest);
    return {
      value: roundedValue,
      unit: to,
      originalValue: value,
      originalUnit: from,
      precision,
    };
  }

  /**
   * Convertit une valeur d'angle
   */
  public convertAngle(
    value: number,
    from: AngleUnit,
    to: AngleUnit,
    options: ConversionOptions = {}
  ): ConversionResult {
    const opts = { ...this.defaultOptions, ...options };
    const degrees = this.toDegrees(value, from);
    const result = this.fromDegrees(degrees, to);
    const precision = opts.precision ?? this.defaultOptions.precision ?? 6;
    const roundedValue = this.roundValue(result, precision, opts.roundToNearest);
    return {
      value: roundedValue,
      unit: to,
      originalValue: value,
      originalUnit: from,
      precision,
    };
  }

  /**
   * Convertit une valeur de temps
   */
  public convertTime(
    value: number,
    from: TimeUnit,
    to: TimeUnit,
    options: ConversionOptions = {}
  ): ConversionResult {
    const opts = { ...this.defaultOptions, ...options };
    const milliseconds = this.toMilliseconds(value, from);
    const result = this.fromMilliseconds(milliseconds, to);
    const precision = opts.precision ?? this.defaultOptions.precision ?? 6;
    const roundedValue = this.roundValue(result, precision, opts.roundToNearest);
    return {
      value: roundedValue,
      unit: to,
      originalValue: value,
      originalUnit: from,
      precision,
    };
  }

  /**
   * Convertit une valeur de poids
   */
  public convertWeight(
    value: number,
    from: WeightUnit,
    to: WeightUnit,
    options: ConversionOptions = {}
  ): ConversionResult {
    const opts = { ...this.defaultOptions, ...options };
    const grams = this.toGrams(value, from);
    const result = this.fromGrams(grams, to);
    const precision = opts.precision ?? this.defaultOptions.precision ?? 6;
    const roundedValue = this.roundValue(result, precision, opts.roundToNearest);
    return {
      value: roundedValue,
      unit: to,
      originalValue: value,
      originalUnit: from,
      precision,
    };
  }

  /**
   * Convertit une valeur de température
   */
  public convertTemperature(
    value: number,
    from: TemperatureUnit,
    to: TemperatureUnit,
    options: ConversionOptions = {}
  ): ConversionResult {
    const opts = { ...this.defaultOptions, ...options };
    const celsius = this.toCelsius(value, from);
    const result = this.fromCelsius(celsius, to);
    const precision = opts.precision ?? this.defaultOptions.precision ?? 6;
    const roundedValue = this.roundValue(result, precision, opts.roundToNearest);
    return {
      value: roundedValue,
      unit: to,
      originalValue: value,
      originalUnit: from,
      precision,
    };
  }

  /**
   * Convertit une valeur avec détection automatique de l'unité
   */
  public convertAuto(
    value: number,
    fromUnit: string,
    toUnit: string,
    options: ConversionOptions = {}
  ): ConversionResult {
    const unitType = this.detectUnitType(fromUnit);
    const targetUnitType = this.detectUnitType(toUnit);

    if (unitType !== targetUnitType) {
      throw new Error(`Types d'unités incompatibles: ${unitType} vs ${targetUnitType}`);
    }

    switch (unitType) {
      case 'length':
        return this.convertLength(value, fromUnit as LengthUnit, toUnit as LengthUnit, options);
      case 'angle':
        return this.convertAngle(value, fromUnit as AngleUnit, toUnit as AngleUnit, options);
      case 'time':
        return this.convertTime(value, fromUnit as TimeUnit, toUnit as TimeUnit, options);
      case 'weight':
        return this.convertWeight(value, fromUnit as WeightUnit, toUnit as WeightUnit, options);
      case 'temperature':
        return this.convertTemperature(
          value,
          fromUnit as TemperatureUnit,
          toUnit as TemperatureUnit,
          options
        );
      default:
        throw new Error(`Type d'unité non supporté: ${unitType}`);
    }
  }

  /**
   * Formate une valeur avec son unité
   */
  public formatValue(result: ConversionResult): string {
    const precision = result.precision || 2;
    const formattedValue = result.value.toFixed(precision);
    return `${formattedValue}${result.unit}`;
  }

  /**
   * Obtient les unités disponibles pour un type
   */
  public getAvailableUnits(type: UnitType): string[] {
    switch (type) {
      case 'length':
        return ['px', 'pt', 'in', 'cm', 'mm', 'pc', 'em', 'rem', 'vw', 'vh', '%'];
      case 'angle':
        return ['deg', 'rad', 'grad', 'turn'];
      case 'time':
        return ['ms', 's', 'min', 'h'];
      case 'weight':
        return ['g', 'kg', 'lb', 'oz'];
      case 'temperature':
        return ['c', 'f', 'k'];
      default:
        return [];
    }
  }

  /**
   * Vérifie si une unité est valide
   */
  public isValidUnit(unit: string, type: UnitType): boolean {
    return this.getAvailableUnits(type).includes(unit);
  }

  /**
   * Détecte le type d'unité
   */
  private detectUnitType(unit: string): UnitType {
    const lengthUnits = ['px', 'pt', 'in', 'cm', 'mm', 'pc', 'em', 'rem', 'vw', 'vh', '%'];
    const angleUnits = ['deg', 'rad', 'grad', 'turn'];
    const timeUnits = ['ms', 's', 'min', 'h'];
    const weightUnits = ['g', 'kg', 'lb', 'oz'];
    const temperatureUnits = ['c', 'f', 'k'];

    if (lengthUnits.includes(unit)) return 'length';
    if (angleUnits.includes(unit)) return 'angle';
    if (timeUnits.includes(unit)) return 'time';
    if (weightUnits.includes(unit)) return 'weight';
    if (temperatureUnits.includes(unit)) return 'temperature';

    throw new Error(`Unité non reconnue: ${unit}`);
  }

  /**
   * Convertit vers les pixels
   */
  private toPixels(value: number, unit: LengthUnit): number {
    const conversions: Record<LengthUnit, number> = {
      px: 1,
      pt: 1.333,
      in: 96,
      cm: 37.8,
      mm: 3.78,
      pc: 16,
      em: 16,
      rem: 16,
      vw: typeof window !== 'undefined' ? window.innerWidth / 100 : 10,
      vh: typeof window !== 'undefined' ? window.innerHeight / 100 : 10,
      '%': 1,
    };
    return value * conversions[unit];
  }

  /**
   * Convertit depuis les pixels
   */
  private fromPixels(pixels: number, unit: LengthUnit): number {
    const conversions: Record<LengthUnit, number> = {
      px: 1,
      pt: 0.75,
      in: 1 / 96,
      cm: 1 / 37.8,
      mm: 1 / 3.78,
      pc: 1 / 16,
      em: 1 / 16,
      rem: 1 / 16,
      vw: typeof window !== 'undefined' ? 100 / window.innerWidth : 0.1,
      vh: typeof window !== 'undefined' ? 100 / window.innerHeight : 0.1,
      '%': 1,
    };
    return pixels * conversions[unit];
  }

  /**
   * Convertit vers les degrés
   */
  private toDegrees(value: number, unit: AngleUnit): number {
    const conversions: Record<AngleUnit, number> = {
      deg: 1,
      rad: 180 / Math.PI,
      grad: 0.9,
      turn: 360,
    };
    return value * conversions[unit];
  }

  /**
   * Convertit depuis les degrés
   */
  private fromDegrees(degrees: number, unit: AngleUnit): number {
    const conversions: Record<AngleUnit, number> = {
      deg: 1,
      rad: Math.PI / 180,
      grad: 1.111,
      turn: 1 / 360,
    };
    return degrees * conversions[unit];
  }

  /**
   * Convertit vers les millisecondes
   */
  private toMilliseconds(value: number, unit: TimeUnit): number {
    const conversions: Record<TimeUnit, number> = {
      ms: 1,
      s: 1000,
      min: 60000,
      h: 3600000,
    };
    return value * conversions[unit];
  }

  /**
   * Convertit depuis les millisecondes
   */
  private fromMilliseconds(milliseconds: number, unit: TimeUnit): number {
    const conversions: Record<TimeUnit, number> = {
      ms: 1,
      s: 0.001,
      min: 1 / 60000,
      h: 1 / 3600000,
    };
    return milliseconds * conversions[unit];
  }

  /**
   * Convertit vers les grammes
   */
  private toGrams(value: number, unit: WeightUnit): number {
    const conversions: Record<WeightUnit, number> = {
      g: 1,
      kg: 1000,
      lb: 453.592,
      oz: 28.3495,
    };
    return value * conversions[unit];
  }

  /**
   * Convertit depuis les grammes
   */
  private fromGrams(grams: number, unit: WeightUnit): number {
    const conversions: Record<WeightUnit, number> = {
      g: 1,
      kg: 0.001,
      lb: 1 / 453.592,
      oz: 1 / 28.3495,
    };
    return grams * conversions[unit];
  }

  /**
   * Convertit vers Celsius
   */
  private toCelsius(value: number, unit: TemperatureUnit): number {
    switch (unit) {
      case 'c':
        return value;
      case 'f':
        return ((value - 32) * 5) / 9;
      case 'k':
        return value - 273.15;
      default:
        throw new Error(`Unité de température non supportée: ${unit}`);
    }
  }

  /**
   * Convertit depuis Celsius
   */
  private fromCelsius(celsius: number, unit: TemperatureUnit): number {
    switch (unit) {
      case 'c':
        return celsius;
      case 'f':
        return (celsius * 9) / 5 + 32;
      case 'k':
        return celsius + 273.15;
      default:
        throw new Error(`Unité de température non supportée: ${unit}`);
    }
  }

  /**
   * Arrondit une valeur
   */
  private roundValue(value: number, precision: number, roundToNearest?: number): number {
    if (roundToNearest) {
      return Math.round(value / roundToNearest) * roundToNearest;
    }
    return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision);
  }

  /**
   * Nettoie les ressources
   */
  public destroy(): void {
    // Nettoyage si nécessaire
  }
}
